/**
 * Unified Visitor Identity — SDK module (plan D19, Flows A/B/C/E/H1/J;
 * "auth-embed SDK" work list). One idempotent `ensureIdentityBootstrap()`
 * shared by `MillaAuth.init` and every brand ad entry point; anonymous
 * `article_viewed` capture behind the bootstrap disposition; WORKOS_LINK and
 * EMAIL_FLOW handoffs; the parent/iframe EMAIL_FLOW handshake; first-party
 * opt-out. The module never sees, stores or emits a visitor id: the
 * credential is an HttpOnly cookie on the brand auth host, and every response
 * it consumes carries only status enums and opaque receipts.
 *
 * Fail-open by design: ads, auth and product modules never wait on identity
 * network success. Only registered engagement capture waits for the
 * bootstrap disposition.
 * @module lib/identity
 */

const { newSessionId, newPageViewId, newFormInstanceId, sha256Ref } = require('./ids');
const { readConsentSignal, writeFirstPartyOptOut, readFirstPartyOptOut, readAdsOptOut, writeAdsOptOut, waitForConsentApi, watchConsentApi } = require('./consent');
const { isTopLevelContext, isPrerendering, isAutomatedClient, isSameSiteWithIssuer, isDocumentHidden, whenDocumentVisible, hostnameOf, resolveArticle } = require('./context');
const { createTransport } = require('./transport');
const { installCrossBrandBridge } = require('./crossBrandBridge');
const { adoptSourceAlias, pendingReplacement } = require('./sourceAliases');

const PRODUCER_VERSION = '2026.09.1';
const ROUTES = Object.freeze({
  bootstrap: '/__milla/identity/bootstrap',
  adoptAliases: '/__milla/identity/aliases/adopt',
  authHandoff: '/__milla/identity/auth-handoff',
  emailHandoff: '/__milla/identity/email-handoff',
  engagementsBatch: '/__milla/engagements/batch',
  consentWithdraw: '/__milla/identity/consent/withdraw',
  accountObserve: '/__milla/identity/account-observe',
  bridgeStart: '/__milla/bridge/start',
  bridgeConsume: '/__milla/bridge/consume',
  consentRestore: '/__milla/identity/consent/restore',
  adsId: '/__milla/identity/ads-id',
  adsConsentWithdraw: '/__milla/identity/ads/consent/withdraw',
  adsConsentRestore: '/__milla/identity/ads/consent/restore'
});
const DISABLED_MARKER_KEY = '__milla_identity_disabled_until__';
const DISABLED_BACKOFF_MS = 10 * 60 * 1000;
const BOOTSTRAP_TIMEOUT_MS = 4000;
const ADOPT_TAG_PATTERN = /^[A-Za-z0-9_-]{22}$/;
const HANDOFF_TIMEOUT_MS = 2500;
const WITHDRAWAL_PENDING_KEY = '__milla_identity_withdrawal_pending__';
const MAX_PENDING_EVENTS = 50;
const HANDOFF_TOKEN_PATTERN = /^[A-Za-z0-9_-]{43}$/;
// Flow G / D18(3) account observation: one request per session while a valid
// WorkOS session is held; a terminal answer (or a rejected token) is remembered
// for this long before the SDK asks again.
const OBSERVED_MARKER_KEY = '__milla_identity_observed_until__';
const OBSERVE_BACKOFF_MS = 10 * 60 * 1000;
const OBSERVE_TERMINAL_STATUSES = ['QUEUED', 'ALREADY_QUEUED', 'ALREADY_LINKED', 'UNLINKED'];
// EEA/US (2026-09-28): Google's CMP reaches the page through GPT, which loads
// after this bootstrap, so the first answer there carries no CMP signal. The
// edge's CMP-jurisdiction policies tell the SDK to wait for the CMP's answer;
// the hint lets a returning browser report "undecided" until it arrives.
const CMP_POLICY_PATTERN = /^publisher-(?:identity|ads)-cmp-v\d+$/;
const CMP_REGION_KEY = '__milla_identity_cmp_region__';
const CONSENT_CHANGE_DEBOUNCE_MS = 250;
// Linkinbio capture (registry 2026-09-04.1): the three frozen triggers the
// Linkinbio app reports through `MillaAuth.identity.capture`. Object ids are
// the app's stable feed slug / feed slug + entry id; the SDK adds brand and
// language and nothing else.
const LINKINBIO_SOURCE_EVENT_TYPES = Object.freeze(['page_view', 'card_view', 'internal_link_click']);
const LINKINBIO_OBJECT_TYPE = 'LINKINBIO_CARD_OR_PAGE';
const LINKINBIO_OBJECT_ID_PATTERN = /^linkinbio:(?:feed|card):[a-z0-9][a-z0-9_-]{0,63}(?::[A-Za-z0-9_-]{1,64})?$/;
const LANGUAGE_PATTERN = /^[a-z]{2}(?:-[A-Z]{2})?$/;

const DEFAULT_FLAGS = Object.freeze({
  bootstrap: true,
  aliasAdoption: true,
  // Replacing a source ID changes anonymous survey/vote continuity. Opt in
  // only after validating that product behavior for the deployment cohort.
  sourceAliasRecovery: false,
  publisherCapture: true,
  authHandoff: true,
  emailHandoff: true,
  accountObservation: true,
  crossBrandBridge: true,
  // Advertising identifiers (plans/ads-identity-unification.md): the derived,
  // consent-gated ads id handed to GAM as PPID. `false` → no PPID anywhere.
  ads: true,
  // How long an EEA/US page waits for Google's CMP before settling on the
  // edge's first answer (GPT, and with it the CMP, loads after the bootstrap).
  cmpWaitMs: 8000,
  // How long the ad stack waits for that CMP answer when the page starts its
  // ads first (GAM reads the PPID once per page); then ads go without an id.
  // It is also the ad delay on a page whose CMP never loads.
  adsCmpWaitMs: 1000,
  debug: false
});

// Derived ads id shape (milla `isAdsId`): 32 lowercase hex. Anything else is
// dropped, never handed to the ad stack.
const ADS_ID_PATTERN = /^[a-f0-9]{32}$/;
const ADS_STATES = ['APPROVED', 'UNDECIDED', 'DENIED', 'DISABLED'];
const ADS_TIMEOUT_MS = 1500;
const noAds = (state, reason) => ({ state, policyVersion: null, source: null, id: null, reason: reason || null });
function normalizeAds(block) {
  if (!block || typeof block !== 'object') return null;
  const state = ADS_STATES.indexOf(block.state) !== -1 ? block.state : 'UNDECIDED';
  const id = state === 'APPROVED' && typeof block.id === 'string' && ADS_ID_PATTERN.test(block.id) ? block.id : null;
  return {
    state,
    policyVersion: typeof block.policyVersion === 'string' ? block.policyVersion : null,
    source: id && (block.source === 'member' || block.source === 'visitor') ? block.source : null,
    id,
    reason: typeof block.reason === 'string' ? block.reason : null
  };
}

function log(level, message, data) {
  if (typeof console === 'undefined') return;
  const fn = console[level] || console.log;
  try {
    if (data === undefined) fn(`[milla-identity] ${message}`);
    else fn(`[milla-identity] ${message}`, data);
  } catch (_) {
    // ignore console failures
  }
}

const initialStatus = () => ({
  phase: 'IDLE', // IDLE | PENDING | SETTLED
  identityState: null, // DURABLE | SESSION_ONLY | BLOCKED_BY_CONSENT | COOKIE_UNAVAILABLE | DISABLED | ERROR
  captureEnabled: false,
  policyVersion: null,
  consentState: null,
  receiptId: null,
  reason: null,
  consentSignal: null
});

const state = {
  issuer: null,
  flags: { ...DEFAULT_FLAGS },
  emitter: null,
  bootstrapPromise: null,
  bootstrapKey: null,
  aliasAdoptionPromise: null,
  aliasAdoptionCache: new Map(),
  // The last bootstrap's one-way credential tag (sourceAliases.js).
  adoptTag: null,
  // Source-alias adoption waits for this page's first accepted engagement
  // batch (adoptAfterCapture); both reset with each new transport/bootstrap.
  captureAccepted: false,
  adoptOnCapture: false,
  // CMP follow-up (awaitCmpSignal / watchConsent): the signals the edge last
  // heard, whether the first answer waits for the CMP, and the page's watcher.
  lastConsent: null,
  awaitingCmp: false,
  cmpSettled: null,
  consentWatcher: null,
  consentRefreshTimer: null,
  consentRefreshPromise: null,
  consentRefreshAgain: false,
  withdrawalPending: false,
  status: initialStatus(),
  sessionId: null,
  pageViewId: null,
  transport: null,
  pendingEvents: [],
  bridgeInstalled: false,
  fetchImpl: null,
  getAccessToken: null,
  observePromise: null,
  observeToken: null,
  observeReadingVersion: null,
  observationMemory: null,
  bootstrapCaptureEnabled: false,
  accountCheckPending: false,
  readingConsentVersion: 0,
  adsConsentVersion: 0,
  // Advertising block from the last bootstrap (visitor-derived) and the
  // member-derived answer memoized per bearer for this page.
  ads: null,
  adsMemberPromise: null,
  adsMemberToken: null,
  capturedObjects: {},
  removeCrossBrandBridge: null
};

function resolveFlags(identityConfig) {
  if (identityConfig === false) {
    return { ...DEFAULT_FLAGS, bootstrap: false, aliasAdoption: false, publisherCapture: false, authHandoff: false, emailHandoff: false, accountObservation: false, crossBrandBridge: false, ads: false };
  }
  if (identityConfig && typeof identityConfig === 'object') return { ...DEFAULT_FLAGS, ...identityConfig };
  return { ...DEFAULT_FLAGS };
}

function issuerOrigin(issuer) {
  try {
    return new URL(issuer).origin;
  } catch (_) {
    return String(issuer || '').replace(/\/$/, '');
  }
}

function doFetch(url, init) {
  const impl = state.fetchImpl || (typeof fetch === 'function' ? fetch : null);
  if (!impl) return Promise.reject(new Error('fetch_unavailable'));
  return impl(url, init);
}

function withTimeout(promise, timeoutMs) {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error('identity_timeout')), timeoutMs);
    promise.then((value) => { clearTimeout(timer); resolve(value); }, (error) => { clearTimeout(timer); reject(error); });
  });
}

// Resolves when `promise` settles or after `timeoutMs`, whichever comes first;
// never rejects.
function settleWithin(promise, timeoutMs) {
  const ms = Number.isFinite(timeoutMs) && timeoutMs > 0 ? timeoutMs : 0;
  if (!ms) return Promise.resolve();
  return new Promise((resolve) => {
    const timer = setTimeout(resolve, ms);
    const done = () => { clearTimeout(timer); resolve(); };
    promise.then(done, done);
  });
}

function sessionStorageGet(key) {
  try {
    return typeof window !== 'undefined' && window.sessionStorage ? window.sessionStorage.getItem(key) : null;
  } catch (_) {
    return null;
  }
}

function sessionStorageSet(key, value) {
  try {
    if (typeof window !== 'undefined' && window.sessionStorage) window.sessionStorage.setItem(key, value);
  } catch (_) {
    // ignore
  }
}

function emit(event, payload) {
  if (state.emitter && typeof state.emitter.emit === 'function') {
    try { state.emitter.emit(event, payload); } catch (_) { /* listener errors never break identity */ }
  }
}

function publicAds() {
  const ads = state.ads;
  if (!ads) return { state: null, policyVersion: null, source: null };
  return { state: ads.state, policyVersion: ads.policyVersion, source: ads.source };
}

function withdrawalPending() {
  try { return state.withdrawalPending || window.localStorage.getItem(WITHDRAWAL_PENDING_KEY) === '1'; } catch (_) { return state.withdrawalPending; }
}

function markWithdrawalPending(pending) {
  state.withdrawalPending = pending;
  try {
    if (pending) window.localStorage.setItem(WITHDRAWAL_PENDING_KEY, '1');
    else window.localStorage.removeItem(WITHDRAWAL_PENDING_KEY);
  } catch (_) { /* retain the in-memory state when storage is unavailable */ }
}

async function finishWithdrawal() {
  if (!state.issuer || !isSameSiteWithIssuer(state.issuer)) return false;
  try {
    const response = await withTimeout(doFetch(`${state.issuer}${ROUTES.consentWithdraw}`, {
      method: 'POST', credentials: 'include', cache: 'no-store', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ schemaVersion: 1 })
    }), HANDOFF_TIMEOUT_MS);
    if (!response.ok) return false;
    markWithdrawalPending(false);
    return true;
  } catch (_) { return false; }
}

function publicStatus() {
  const { phase, identityState, captureEnabled, policyVersion, consentState, receiptId, reason } = state.status;
  // The ads id itself never appears here — only the ad stack reads it (getAdsIdentity).
  return { phase, identityState, captureEnabled, policyVersion, consentState, receiptId, reason, erasurePending: withdrawalPending(), ads: publicAds() };
}

/**
 * Called from `MillaAuth.init` (and lazily from the ad entry points when init
 * was skipped). Only issuer and the `identity` flags are read.
 */
function configure(sdkConfig = {}, { emitter = null, fetchImpl = null, getAccessToken = null } = {}) {
  state.issuer = issuerOrigin(sdkConfig.issuer);
  state.flags = resolveFlags(sdkConfig.identity);
  if (emitter) state.emitter = emitter;
  if (fetchImpl) state.fetchImpl = fetchImpl;
  if (typeof getAccessToken === 'function') state.getAccessToken = getAccessToken;
  if (!state.sessionId) state.sessionId = newSessionId();
  if (!state.pageViewId) state.pageViewId = newPageViewId();
  return state.flags;
}

function settle(patch) {
  state.status = { ...state.status, ...patch, phase: 'SETTLED' };
  state.bootstrapCaptureEnabled = state.status.identityState === 'DURABLE' && state.status.captureEnabled;
  const signedIn = typeof state.getAccessToken === 'function' && state.getAccessToken();
  if (state.bootstrapCaptureEnabled && signedIn && state.flags.accountObservation) state.accountCheckPending = true;
  emit('identity:ready', publicStatus());
  if (state.bootstrapCaptureEnabled && !state.accountCheckPending) startCapture();
  else if (state.accountCheckPending) { /* retain pending events until the account check completes */ }
  else dropPending(state.status.reason || state.status.identityState || 'not_capturing');
  // Flow G: a page that already holds a session observes it now; a login that
  // happens later re-enters through observeAuthenticatedSession() on `token`.
  if (state.status.identityState === 'DURABLE') Promise.resolve(observeAuthenticatedSession()).catch(() => {});
  if (state.status.identityState === 'DURABLE' && !state.removeCrossBrandBridge) {
    state.removeCrossBrandBridge = installCrossBrandBridge({ getState: () => state, route: ROUTES.bridgeStart, isOptedOut: readFirstPartyOptOut });
  }
  return publicStatus();
}

function dropPending(reason) {
  if (state.pendingEvents.length && state.flags.debug) log('log', 'dropping pending events', { count: state.pendingEvents.length, reason });
  state.pendingEvents = [];
}

function startCapture() {
  if (!state.flags.publisherCapture) {
    dropPending('flag_off');
    return;
  }
  if (!state.transport) {
    state.captureAccepted = false;
    state.transport = createTransport({
      endpoint: () => `${state.issuer}${ROUTES.engagementsBatch}`,
      producerVersion: PRODUCER_VERSION,
      sessionId: state.sessionId,
      fetchImpl: state.fetchImpl,
      onDisabled: (reason) => {
        state.status = { ...state.status, captureEnabled: false, reason };
        emit('identity:capture-disabled', { reason });
      },
      onAccepted: () => {
        state.captureAccepted = true;
        if (state.adoptOnCapture) { state.adoptOnCapture = false; adoptKnownAliases().catch(() => {}); }
      },
      log: state.flags.debug ? log : null
    });
    state.transport.hookUnload();
  }
  const pending = state.pendingEvents;
  state.pendingEvents = [];
  pending.forEach((event) => state.transport.enqueue(event));
}

function ineligible(reason) {
  state.status = { ...initialStatus(), phase: 'SETTLED', identityState: 'DISABLED', reason };
  emit('identity:ready', publicStatus());
  dropPending(reason);
  return publicStatus();
}

async function runBootstrap() {
  state.aliasAdoptionPromise = null;
  state.aliasAdoptionCache = new Map();
  state.adoptTag = null;
  state.adoptOnCapture = false;
  state.awaitingCmp = false;
  const readingVersion = state.readingConsentVersion;
  const adsVersion = state.adsConsentVersion;
  if (!state.flags.bootstrap) return ineligible('flag_off');
  if (typeof window === 'undefined' || typeof fetch !== 'function' && !state.fetchImpl) return ineligible('no_browser');
  if (!state.issuer) return ineligible('no_issuer');
  if (!isTopLevelContext()) return ineligible('not_top_level');
  if (isAutomatedClient()) return ineligible('automated_client');
  if (!isSameSiteWithIssuer(state.issuer)) return ineligible('not_same_site');
  if (isPrerendering()) {
    // A prerendered page must not mint; retry once it is actually shown.
    await new Promise((resolve) => document.addEventListener('prerenderingchange', resolve, { once: true }));
  }
  if (isDocumentHidden()) {
    // 2026-09-22: a hidden document is a WebKit preload/prefetch or a background
    // tab. Mint nothing until it is shown; article views queue meanwhile and
    // flush after the bootstrap settles, so a shown page loses nothing.
    state.status = { ...state.status, phase: 'PENDING', reason: 'awaiting_visibility' };
    await whenDocumentVisible();
    if (state.readingConsentVersion !== readingVersion) return publicStatus();
  }
  const disabledUntil = Number(sessionStorageGet(DISABLED_MARKER_KEY));
  if (Number.isFinite(disabledUntil) && disabledUntil > Date.now()) return ineligible('disabled_cached');

  // Do not let a denied bootstrap clear the only credential capable of
  // retrying an explicit withdrawal that failed on the previous page.
  if (withdrawalPending() && !await finishWithdrawal()) {
    return settle({ identityState: 'BLOCKED_BY_CONSENT', captureEnabled: false, reason: 'withdrawal_pending' });
  }

  let consent = await bootstrapConsent();
  if (state.readingConsentVersion !== readingVersion) return publicStatus();
  // A browser last answered under an EEA/US policy reports "undecided" until
  // the CMP loads, so the edge mints nothing before the reader's real choice.
  if (consent.regime === 'none' && cmpRegionHinted()) consent = { ...consent, signal: 'undecided', adsSignal: consent.adsSignal === 'denied' ? 'denied' : 'undecided' };
  state.status = { ...state.status, phase: 'PENDING', consentSignal: consent.signal };
  try {
    const { response, data } = await requestBootstrap(consent);
    // A consent change can finish while this older request is in flight. Its
    // answer must not restart capture after withdrawal or restore an old id.
    if (state.readingConsentVersion !== readingVersion) return publicStatus();
    if (!response.ok) {
      const retryable = response.status === 429 || response.status >= 500;
      return settle({ identityState: retryable ? 'ERROR' : 'DISABLED', captureEnabled: false, reason: (data && (data.error || data.reason)) || `http_${response.status}` });
    }
    state.lastConsent = consent;
    const cmpRegion = isCmpAnswer(data);
    rememberCmpRegion(cmpRegion);
    if (cmpRegion && consent.regime === 'none') return awaitCmpSignal(data, { readingVersion, adsVersion });
    const settled = applyBootstrapAnswer(data, adsVersion);
    if (consent.regime === 'tcf' || consent.regime === 'gpp') watchConsent();
    return settled;
  } catch (error) {
    if (state.readingConsentVersion !== readingVersion) return publicStatus();
    return settle({ identityState: 'ERROR', captureEnabled: false, reason: error && error.message === 'identity_timeout' ? 'timeout' : 'network' });
  }
}

// The two signals one bootstrap carries: the CMP's (or first-party) reading,
// with the SDK's own ads flag folded in.
async function bootstrapConsent() {
  const consent = await readConsentSignal();
  return { ...consent, adsSignal: state.flags.ads === false ? 'denied' : (consent.adsSignal || 'not_applicable') };
}

const sameConsent = (a, b) => Boolean(a && b) && a.signal === b.signal && a.adsSignal === b.adsSignal;
const isCmpAnswer = (data) => [data && data.policyVersion, data && data.ads && data.ads.policyVersion].some((value) => typeof value === 'string' && CMP_POLICY_PATTERN.test(value));

function cmpRegionHinted() {
  try { return window.localStorage.getItem(CMP_REGION_KEY) === '1'; } catch (_) { return false; }
}

function rememberCmpRegion(cmpRegion) {
  try {
    if (cmpRegion) window.localStorage.setItem(CMP_REGION_KEY, '1');
    else window.localStorage.removeItem(CMP_REGION_KEY);
  } catch (_) { /* no storage: each page waits on its first answer instead */ }
}

async function requestBootstrap(consent) {
  // `pk` (2026-09-22): is this an article page? Counted at the edge next to
  // the browser family so a fresh-mint population that never captures an
  // article view can be told apart from readers (the review-queue conflicts).
  const url = `${state.issuer}${ROUTES.bootstrap}?cs=${encodeURIComponent(consent.signal)}&cr=${encodeURIComponent(consent.regime)}&as=${encodeURIComponent(consent.adsSignal)}&pk=${pageKind()}`;
  const response = await withTimeout(doFetch(url, { method: 'GET', credentials: 'include', cache: 'no-store', headers: { Accept: 'application/json' } }), BOOTSTRAP_TIMEOUT_MS);
  const data = await response.json().catch(() => ({}));
  return { response, data };
}

// One bootstrap answer: advertising block, disposition, capture and source-alias
// adoption. Also applied after a re-bootstrap that carries the CMP's signal.
function applyBootstrapAnswer(data, adsVersion) {
  state.awaitingCmp = false;
  const identityState = typeof data.identityState === 'string' ? data.identityState : 'ERROR';
  if (identityState === 'DISABLED') sessionStorageSet(DISABLED_MARKER_KEY, String(Date.now() + DISABLED_BACKOFF_MS));
  if (state.adsConsentVersion === adsVersion) state.ads = state.flags.ads === false ? noAds('DISABLED', 'sdk_ads_off') : (normalizeAds(data.ads) || noAds('UNDECIDED', 'ads_block_missing'));
  const captureEnabled = identityState === 'DURABLE' && data.captureEnabled === true;
  state.adoptTag = identityState === 'DURABLE' && ADOPT_TAG_PATTERN.test(String(data.adoptTag || '')) ? data.adoptTag : null;
  if (!captureEnabled && state.transport) {
    // A later CMP choice withdrew capture: stop sending under the old answer.
    state.transport.disable('consent_changed');
    state.transport = null;
  }
  const settled = settle({
    identityState,
    captureEnabled,
    policyVersion: data.policyVersion || null,
    consentState: data.consentState || null,
    receiptId: data.receiptId || null,
    reason: data.reason || null
  });
  if (identityState === 'DURABLE') adoptAfterCapture();
  return settled;
}

// EEA/US answer given without a CMP signal (2026-09-28 regional check): the ad
// stack gets no PPID and article views stay queued until Google's CMP answers;
// the edge is asked again when that answer differs from what it heard. A US
// opt-out then revokes the credential; EEA consent unlocks the identity.
function awaitCmpSignal(firstAnswer, { readingVersion, adsVersion }) {
  state.awaitingCmp = true;
  if (state.adsConsentVersion === adsVersion) state.ads = noAds('UNDECIDED', 'awaiting_cmp');
  state.status = { ...state.status, phase: 'PENDING', reason: 'awaiting_cmp', policyVersion: firstAnswer.policyVersion || null };
  const current = () => state.awaitingCmp && state.readingConsentVersion === readingVersion && state.adsConsentVersion === adsVersion;
  const waitMs = Number.isFinite(state.flags.cmpWaitMs) ? state.flags.cmpWaitMs : DEFAULT_FLAGS.cmpWaitMs;
  state.cmpSettled = waitForConsentApi({ timeoutMs: waitMs }).then(async (found) => {
    if (!current()) return;
    // No CMP ever answered (blocked, or a surface without GPT): the first answer stands.
    if (!found) { applyBootstrapAnswer(firstAnswer, adsVersion); return; }
    const next = await bootstrapConsent();
    if (!current()) return;
    if (sameConsent(next, state.lastConsent)) applyBootstrapAnswer(firstAnswer, adsVersion);
    else await refreshBootstrap(next, { readingVersion, adsVersion, fallback: firstAnswer });
    watchConsent();
  }).catch(() => {
    if (!current()) return;
    state.awaitingCmp = false;
    settle({ identityState: 'SESSION_ONLY', captureEnabled: false, reason: 'cmp_unavailable' });
  });
  return publicStatus();
}

// Re-ask the edge with the CMP's signal. A failed request never falls back to
// an approval the reader may have withdrawn.
async function refreshBootstrap(consent, { readingVersion, adsVersion, fallback = null }) {
  const current = () => state.readingConsentVersion === readingVersion && state.adsConsentVersion === adsVersion;
  state.status = { ...state.status, consentSignal: consent.signal };
  try {
    const { response, data } = await requestBootstrap(consent);
    if (!current()) return;
    if (!response.ok) throw new Error(`http_${response.status}`);
    state.lastConsent = consent;
    rememberCmpRegion(isCmpAnswer(data));
    applyBootstrapAnswer(data, adsVersion);
  } catch (_) {
    if (!current()) return;
    const withdrawn = consent.signal === 'denied' || consent.adsSignal === 'denied';
    if (fallback && !withdrawn) applyBootstrapAnswer(fallback, adsVersion);
    else {
      if (state.adsConsentVersion === adsVersion) state.ads = noAds(consent.adsSignal === 'denied' ? 'DENIED' : 'UNDECIDED', 'cmp_refresh_failed');
      if (state.transport) { state.transport.disable('consent_changed'); state.transport = null; }
      state.awaitingCmp = false;
      settle({ identityState: consent.signal === 'denied' ? 'BLOCKED_BY_CONSENT' : 'SESSION_ONLY', captureEnabled: false, reason: 'cmp_refresh_failed' });
    }
  }
  // The ad stack reads window.ppid when it initializes; drop a stale one.
  if (typeof window !== 'undefined' && (!state.ads || state.ads.state !== 'APPROVED')) window.ppid = null;
  emit('identity:ads', publicAds());
}

// Follow the CMP's later decisions on this page: a reader who consents or opts
// out in Google's message is re-bootstrapped with the new signal.
function watchConsent() {
  if (state.consentWatcher) return;
  state.consentWatcher = watchConsentApi(() => {
    if (state.consentRefreshTimer) return;
    state.consentRefreshTimer = setTimeout(() => {
      state.consentRefreshTimer = null;
      followConsentChange();
    }, CONSENT_CHANGE_DEBOUNCE_MS);
    if (state.consentRefreshTimer && typeof state.consentRefreshTimer.unref === 'function') state.consentRefreshTimer.unref();
  });
}

function followConsentChange() {
  if (state.consentRefreshPromise) { state.consentRefreshAgain = true; return state.consentRefreshPromise; }
  const readingVersion = state.readingConsentVersion;
  const adsVersion = state.adsConsentVersion;
  state.consentRefreshPromise = (async () => {
    if (state.status.phase !== 'SETTLED' || state.awaitingCmp || state.status.identityState === 'DISABLED') return;
    const next = await bootstrapConsent();
    if (state.readingConsentVersion !== readingVersion || state.adsConsentVersion !== adsVersion) return;
    if (sameConsent(next, state.lastConsent)) return;
    await refreshBootstrap(next, { readingVersion, adsVersion });
  })().catch(() => {}).finally(() => {
    state.consentRefreshPromise = null;
    if (state.consentRefreshAgain) { state.consentRefreshAgain = false; followConsentChange(); }
  });
  return state.consentRefreshPromise;
}

/**
 * The shared bootstrap promise (D19): every entry point deduplicates to one
 * in-flight/completed bootstrap per page context and issuer.
 */
function ensureIdentityBootstrap() {
  const key = `${state.issuer}|${state.flags.bootstrap}`;
  if (state.bootstrapPromise && state.bootstrapKey === key) return state.bootstrapPromise;
  state.bootstrapKey = key;
  state.bootstrapPromise = runBootstrap().catch((error) => {
    if (state.flags.debug) log('warn', 'bootstrap failed', error);
    return settle({ identityState: 'ERROR', captureEnabled: false, reason: 'unexpected' });
  });
  return state.bootstrapPromise;
}

function getIdentityStatus() {
  return publicStatus();
}

// --- Flow C: adopt the Insights anon id this browser already holds ------------

// Bounded page kind for edge counters: `article` when the frozen article
// trigger resolves, else `other` (home, section, tag, product pages).
function pageKind() {
  try { return resolveArticle() ? 'article' : 'other'; } catch (_) { return 'other'; }
}

// 2026-09-28: adopt the Insights id only once this page has shown that it keeps
// its credential, i.e. at its first accepted engagement batch. Every review-queue
// conflict came from a bootstrap that minted, adopted a survey id owned by an
// older root and never captured anything (0 of 955 conflicting visitors had an
// event on 2026-09-28 08–14 UTC, against 99.4 % of successful adopters): non-
// article landings and pages gone before the first flush. A page that will not
// capture adopts at once, as before, and so does the opt-in source-recovery
// flow, whose reviewed repair timing is unchanged.
function adoptAfterCapture() {
  const immediate = state.flags.sourceAliasRecovery === true || !state.flags.publisherCapture || !state.bootstrapCaptureEnabled || state.captureAccepted;
  if (immediate) adoptKnownAliases().catch(() => {});
  else state.adoptOnCapture = true;
}

function adoptKnownAliases() {
  if (!state.flags.aliasAdoption || state.status.identityState !== 'DURABLE') return Promise.resolve(null);
  if (state.aliasAdoptionPromise) return state.aliasAdoptionPromise;
  const issuer = state.issuer;
  const readingVersion = state.readingConsentVersion;
  const brand = /mtlblog/i.test(hostnameOf(issuer)) ? 'mtlblog' : 'narcity';
  const cache = state.aliasAdoptionCache;
  const recover = state.flags.sourceAliasRecovery === true;
  const isCurrent = () => state.flags.bootstrap && state.flags.aliasAdoption && (state.flags.sourceAliasRecovery === true) === recover && state.issuer === issuer && state.readingConsentVersion === readingVersion && state.aliasAdoptionCache === cache && state.status.identityState === 'DURABLE';
  // Adoption goes as text/plain JSON: a CORS simple request, no preflight.
  // The Insights cookie sync (recovery only) has no Origin guard and stays JSON.
  const request = (route, body) => withTimeout(doFetch(`${issuer}${route}`, {
    method: 'POST', credentials: 'include', cache: 'no-store',
    headers: { 'Content-Type': route === ROUTES.adoptAliases ? 'text/plain;charset=UTF-8' : 'application/json' }, body: JSON.stringify(body)
  }).then(async (response) => response.ok ? response.json() : null), HANDOFF_TIMEOUT_MS);
  const pending = adoptSourceAlias({ brand, issuer, request, isCurrent, cache, recover, pageKind: pageKind(), tag: state.adoptTag });
  state.aliasAdoptionPromise = pending;
  pending.finally(() => { if (state.aliasAdoptionPromise === pending) state.aliasAdoptionPromise = null; });
  return pending;
}

// Survey creation waits for source-id repair, while article capture remains
// independent. A pending repair may not write an impression under the old id.
async function prepareInsightsIdentity(brand) {
  if (!state.issuer || !state.flags.bootstrap || !state.flags.aliasAdoption || state.flags.sourceAliasRecovery !== true) return true;
  const identityBrand = /mtlblog/i.test(hostnameOf(state.issuer)) ? 'mtlblog' : 'narcity';
  if (brand !== identityBrand) return true;
  await ensureIdentityBootstrap();
  // Identity consent and availability must not disable the separate survey
  // product. Keep the journal so a later eligible bootstrap can resume repair.
  if (state.status.identityState !== 'DURABLE') return true;
  await adoptKnownAliases();
  return state.status.identityState !== 'DURABLE' || !state.flags.aliasAdoption || state.flags.sourceAliasRecovery !== true || !pendingReplacement(brand);
}

// --- Flow B: registered capture -----------------------------------------------

function trackEvent(sourceEventType, object, context = {}) {
  if (!state.flags.publisherCapture || !state.flags.bootstrap) return false;
  const event = { sourceEventType, object, occurredAt: new Date().toISOString(), context: { pageViewId: state.pageViewId, ...context } };
  if (state.status.phase === 'SETTLED' && !state.accountCheckPending) {
    if (state.status.identityState !== 'DURABLE' || !state.status.captureEnabled || !state.transport) return false;
    state.transport.enqueue(event);
    return true;
  }
  if (state.pendingEvents.length >= MAX_PENDING_EVENTS) state.pendingEvents.shift();
  state.pendingEvents.push(event);
  ensureIdentityBootstrap();
  return true;
}

function trackedArticles() {
  if (typeof window === 'undefined') return {};
  window.__millaIdentityArticleTracked = window.__millaIdentityArticleTracked || {};
  return window.__millaIdentityArticleTracked;
}

/**
 * Frozen www trigger: ARTICLE_PAGE_ONCE_PER_ARTICLE_ID_PER_PAGE_LOAD, for any
 * visitor (the authenticated history write stays a separate, member-scoped
 * event). Returns the reason it skipped, or 'queued'.
 */
function captureArticleView() {
  const article = resolveArticle();
  if (!article) return 'not-article-page';
  const tracked = trackedArticles();
  if (tracked[article.id]) return 'already-tracked';
  tracked[article.id] = true;
  return trackEvent('article_viewed', article) ? 'queued' : 'not-capturing';
}

// --- Flow E: WORKOS_LINK handoff -------------------------------------------------

async function requestAuthHandoff(authState) {
  if (!state.flags.authHandoff || !authState) return null;
  try {
    const status = await ensureIdentityBootstrap();
    if (status.identityState !== 'DURABLE') return null;
    const authStateHash = await sha256Ref(authState);
    if (!authStateHash) return null;
    const response = await withTimeout(doFetch(`${state.issuer}${ROUTES.authHandoff}`, {
      method: 'POST',
      credentials: 'include',
      cache: 'no-store',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ schemaVersion: 1, purpose: 'WORKOS_LINK', authStateHash })
    }), HANDOFF_TIMEOUT_MS);
    if (!response.ok) return null;
    const data = await response.json().catch(() => ({}));
    return typeof data.handoffToken === 'string' && HANDOFF_TOKEN_PATTERN.test(data.handoffToken) ? data.handoffToken : null;
  } catch (_) {
    return null;
  }
}

// --- Flow H1: EMAIL_FLOW handoff + parent/iframe handshake ----------------------

async function requestEmailHandoff({ flow, formInstanceId, embedNonce = null } = {}) {
  if (!state.flags.emailHandoff) return null;
  if (!['NEWSLETTER', 'CONTEST', 'INSIGHTS'].includes(flow)) return null;
  try {
    const status = await ensureIdentityBootstrap();
    if (status.identityState !== 'DURABLE') return null;
    const body = { schemaVersion: 1, flow, formInstanceId: formInstanceId || newFormInstanceId() };
    if (embedNonce) body.embedNonce = embedNonce;
    const response = await withTimeout(doFetch(`${state.issuer}${ROUTES.emailHandoff}`, {
      method: 'POST',
      credentials: 'include',
      cache: 'no-store',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    }), HANDOFF_TIMEOUT_MS);
    if (!response.ok) return null;
    const data = await response.json().catch(() => ({}));
    return typeof data.handoffToken === 'string' && HANDOFF_TOKEN_PATTERN.test(data.handoffToken) ? data.handoffToken : null;
  } catch (_) {
    return null;
  }
}

function isChildFrameSource(source) {
  if (typeof document === 'undefined' || !source) return false;
  try {
    const frames = Array.from(document.getElementsByTagName('iframe'));
    return frames.some((frame) => frame && frame.contentWindow === source);
  } catch (_) {
    return false;
  }
}

const ID_TOKEN_PATTERN = /^[A-Za-z0-9_-]{8,128}$/;

/**
 * Parent side of the EMAIL_FLOW handshake: an auth-host iframe we embed asks
 * for a handoff with a nonce; we answer only that frame, only at the issuer
 * origin, with the opaque token (never a stable id).
 */
function installEmailHandoffBridge() {
  if (state.bridgeInstalled || typeof window === 'undefined' || typeof window.addEventListener !== 'function') return;
  state.bridgeInstalled = true;
  window.addEventListener('message', (event) => {
    if (!event || !event.data || event.data.type !== 'MILLA_EMAIL_FLOW_HANDOFF_REQUEST') return;
    if (!state.issuer || event.origin !== state.issuer) return;
    if (!isChildFrameSource(event.source)) return;
    const { flow, formInstanceId, nonce } = event.data;
    if (!ID_TOKEN_PATTERN.test(String(nonce || '')) || !ID_TOKEN_PATTERN.test(String(formInstanceId || ''))) return;
    requestEmailHandoff({ flow, formInstanceId, embedNonce: nonce }).then((token) => {
      try {
        event.source.postMessage({ type: 'MILLA_EMAIL_FLOW_HANDOFF', nonce, token: token || null }, event.origin);
      } catch (_) {
        // frame navigated away
      }
    });
  });
}

// --- Flow G / D18(3): same-origin account observation ---------------------------

/**
 * While the SDK holds a valid WorkOS session on a DURABLE identity, ask the
 * brand auth host (same-site) to attach this surface's existing visitor alias
 * to the account. Sends the access token as a bearer and the cookie with
 * `credentials: 'include'`; learns only a status enum. One request per
 * session, remembered for OBSERVE_BACKOFF_MS after a terminal or rejected
 * answer; cleared on logout so the rotated visitor is observed again.
 */
async function observeAuthenticatedSession() {
  if (!state.flags.accountObservation || !state.flags.bootstrap) return null;
  if (typeof window === 'undefined' || !state.issuer || !isSameSiteWithIssuer(state.issuer)) return null;
  const token = typeof state.getAccessToken === 'function' ? state.getAccessToken() : null;
  if (typeof token !== 'string' || !token) return null;
  if (state.observePromise) {
    if (state.observeToken === token && state.observeReadingVersion === state.readingConsentVersion) return state.observePromise;
    await state.observePromise;
    return observeAuthenticatedSession();
  }
  state.observeToken = token;
  const readingVersion = state.readingConsentVersion;
  state.observeReadingVersion = readingVersion;
  state.observePromise = (async () => {
    try {
      const status = await ensureIdentityBootstrap();
      if (status.identityState !== 'DURABLE') return null;
      const fingerprint = await sha256Ref(token);
      if (state.getAccessToken() !== token || state.readingConsentVersion !== readingVersion) return null;
      let stored = null;
      try { stored = JSON.parse(sessionStorageGet(OBSERVED_MARKER_KEY)); } catch (_) { /* legacy timestamp is not an account binding */ }
      const cached = state.observationMemory?.token === token ? state.observationMemory
        : fingerprint && stored?.fingerprint === fingerprint ? stored : null;
      if (cached?.until > Date.now()) {
        state.accountCheckPending = false;
        const resume = cached.safe && state.bootstrapCaptureEnabled && state.status.identityState === 'DURABLE' && !readFirstPartyOptOut() && !withdrawalPending();
        state.status = { ...state.status, captureEnabled: Boolean(resume) };
        if (resume) startCapture();
        else dropPending('account_check_rejected');
        return null;
      }
      // Drop queued old-account events while checking the new session. Failures
      // leave capture stopped; auth and the page remain usable.
      if (state.transport) {
        state.transport.disable('account_check_pending');
        dropPending('account_check_pending');
      }
      state.transport = null;
      state.accountCheckPending = true;
      state.status = { ...state.status, captureEnabled: false, reason: 'account_check_pending' };
      const response = await withTimeout(doFetch(`${state.issuer}${ROUTES.accountObserve}`, {
        method: 'POST',
        credentials: 'include',
        cache: 'no-store',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ schemaVersion: 1 })
      }), HANDOFF_TIMEOUT_MS);
      const data = await response.json().catch(() => ({}));
      if (state.getAccessToken() !== token || state.readingConsentVersion !== readingVersion) return null;
      state.accountCheckPending = false;
      const terminal = response.ok && OBSERVE_TERMINAL_STATUSES.includes(data.status);
      const safe = terminal && ['QUEUED', 'ALREADY_QUEUED', 'ALREADY_LINKED'].includes(data.status);
      if (terminal || response.status === 401 || response.status === 429) {
        const marker = { fingerprint, until: Date.now() + OBSERVE_BACKOFF_MS, safe };
        state.observationMemory = { ...marker, token };
        if (fingerprint) sessionStorageSet(OBSERVED_MARKER_KEY, JSON.stringify(marker));
      }
      const result = { status: terminal ? data.status : 'ERROR', reason: data.unlinkedReason || data.error || null };
      if (terminal && data.unlinkedReason === 'ACCOUNT_SWITCH') {
        dropPending('account_switch');
        state.ads = null;
        state.adsMemberPromise = null;
        state.adsMemberToken = null;
        forgetAccountObservation();
        // The edge has cleared the revoked credential. Reload resets every ad
        // adapter and starts a fresh visitor before any more article capture.
        window.ppid = null;
        if (typeof window.location?.reload === 'function') window.location.reload();
      } else if (safe && state.bootstrapCaptureEnabled && state.status.identityState === 'DURABLE' && !readFirstPartyOptOut() && !withdrawalPending()) {
        state.status = { ...state.status, captureEnabled: true, reason: null };
        startCapture();
      } else dropPending('account_check_rejected');
      emit('identity:account-observed', result);
      return result;
    } catch (_) {
      state.accountCheckPending = false;
      dropPending('account_check_failed');
      return null;
    } finally {
      state.observePromise = null;
    }
  })();
  return state.observePromise;
}

function forgetAccountObservation() {
  state.observationMemory = null;
  try {
    if (typeof window !== 'undefined' && window.sessionStorage) window.sessionStorage.removeItem(OBSERVED_MARKER_KEY);
  } catch (_) {
  }
}

// --- Linkinbio capture (registry 2026-09-04.1) ------------------------------------

/**
 * `MillaAuth.identity.capture(type, { id, language })` for the Linkinbio app:
 * one of the three frozen triggers, on a linkinbio.* page only, once per
 * (type, id) per page load. The object carries the app's stable id plus brand
 * (from the issuer) and language; no URL, title or card text ever leaves the
 * page. Returns 'queued' or the reason it skipped.
 */
function capture(sourceEventType, object = {}) {
  if (typeof window === 'undefined') return 'no-browser';
  const pageHost = String((window.location && window.location.hostname) || '').toLowerCase();
  if (!/^linkinbio\./.test(pageHost)) return 'not-linkinbio-surface';
  if (!LINKINBIO_SOURCE_EVENT_TYPES.includes(sourceEventType)) return 'unregistered-type';
  const id = object && typeof object.id === 'string' ? object.id.trim() : '';
  if (!LINKINBIO_OBJECT_ID_PATTERN.test(id)) return 'invalid-id';
  const key = `${sourceEventType}|${id}`;
  if (state.capturedObjects[key]) return 'already-tracked';
  state.capturedObjects[key] = true;
  const brand = /mtlblog/i.test(hostnameOf(state.issuer)) ? 'mtlblog' : 'narcity';
  const requested = object && typeof object.language === 'string' ? object.language : null;
  const documentLanguage = typeof document !== 'undefined' && document.documentElement && document.documentElement.lang ? String(document.documentElement.lang).slice(0, 5) : null;
  const language = [requested, documentLanguage].find((value) => value && LANGUAGE_PATTERN.test(value)) || null;
  return trackEvent(sourceEventType, { type: LINKINBIO_OBJECT_TYPE, id, snapshot: { brand, language } }) ? 'queued' : 'not-capturing';
}

// --- Advertising identifiers (plans/ads-identity-unification.md) ---------------------

/**
 * The `ads` block for the ad stack: `{ state, policyVersion, source, id, reason }`.
 * Member-derived when the SDK holds a valid access token (one memoized
 * same-site call to the ads-id route per bearer per page), otherwise the
 * visitor-derived block from the bootstrap. Never throws; a failure or an
 * undecided CMP answers without an id, so the ad stack sets no PPID.
 */
async function getAdsIdentity() {
  const adsVersion = state.adsConsentVersion;
  if (state.flags.ads === false || !state.flags.bootstrap) return noAds('DISABLED', 'sdk_ads_off');
  if (readAdsOptOut()) return noAds('DENIED', 'first_party_ads_opt_out');
  await ensureIdentityBootstrap();
  // The page starts its ads on DOMContentLoaded, which can come before
  // Google's CMP answers (2026-09-28 post-deploy check: 4 of 20 US loads,
  // 76–116 ms early). Wait briefly for that answer rather than hand the ad
  // stack the interim no-id block.
  if (state.awaitingCmp && state.cmpSettled) await settleWithin(state.cmpSettled, state.flags.adsCmpWaitMs);
  if (readAdsOptOut()) return noAds('DENIED', 'first_party_ads_opt_out');
  if (state.adsConsentVersion !== adsVersion) return noAds('UNDECIDED', 'consent_changed');
  const visitor = state.ads || noAds('UNDECIDED', 'bootstrap_unsettled');
  const token = typeof state.getAccessToken === 'function' ? state.getAccessToken() : null;
  if (typeof token !== 'string' || !token || visitor.state !== 'APPROVED') return visitor;
  if (!state.issuer || !isSameSiteWithIssuer(state.issuer)) return visitor;
  if (!state.adsMemberPromise || state.adsMemberToken !== token) {
    state.adsMemberToken = token;
    state.adsMemberPromise = (async () => {
      const consent = await readConsentSignal();
      const adsSignal = consent.adsSignal || 'not_applicable';
      const url = `${state.issuer}${ROUTES.adsId}?as=${encodeURIComponent(adsSignal)}&cr=${encodeURIComponent(consent.regime)}`;
      const response = await withTimeout(doFetch(url, {
        method: 'GET', credentials: 'include', cache: 'no-store', headers: { Accept: 'application/json', Authorization: `Bearer ${token}` }
      }), ADS_TIMEOUT_MS);
      if (!response.ok) throw new Error(`http_${response.status}`);
      const data = await response.json().catch(() => ({}));
      const block = normalizeAds(data.ads);
      if (!block) throw new Error('ads_block_missing');
      return block;
    })().catch((error) => {
      if (state.flags.debug) log('warn', 'ads-id lookup failed', error && error.message);
      if (state.adsMemberToken === token) state.adsMemberPromise = null;
      return null;
    });
  }
  const member = await state.adsMemberPromise;
  if (readAdsOptOut()) return noAds('DENIED', 'first_party_ads_opt_out');
  if (state.adsConsentVersion !== adsVersion) return noAds('UNDECIDED', 'consent_changed');
  if (state.getAccessToken() !== token) return noAds('UNDECIDED', 'account_changed');
  // A verified login can belong to a different account than the bootstrap's
  // registered visitor. Never recover a failed/empty member lookup with that
  // previous account's advertising identifier.
  const chosen = member || noAds('UNDECIDED', 'member_lookup_failed');
  emit('identity:ads', publicAdsOf(chosen));
  return chosen;
}

function publicAdsOf(block) {
  return { state: block.state, policyVersion: block.policyVersion, source: block.source };
}

function getAdsStatus() {
  return publicAds();
}

/** First-party advertising opt-out: no PPID and no Prebid id modules from the next page view. */
async function adsOptOut() {
  state.adsConsentVersion += 1;
  writeAdsOptOut(true);
  state.ads = noAds('DENIED', 'first_party_ads_opt_out');
  state.adsMemberPromise = null;
  emit('identity:ads', publicAds());
  if (!state.issuer || !isSameSiteWithIssuer(state.issuer)) return publicAds();
  try {
    await withTimeout(doFetch(`${state.issuer}${ROUTES.adsConsentWithdraw}`, {
      method: 'POST', credentials: 'include', cache: 'no-store', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ schemaVersion: 1 })
    }), HANDOFF_TIMEOUT_MS);
  } catch (_) {
    // the local marker already blocks the id; the auth host cookie follows on the next attempt
  }
  return publicAds();
}

async function adsOptIn() {
  state.adsConsentVersion += 1;
  writeAdsOptOut(false);
  if (state.issuer && isSameSiteWithIssuer(state.issuer)) {
    try {
      await withTimeout(doFetch(`${state.issuer}${ROUTES.adsConsentRestore}`, {
        method: 'POST', credentials: 'include', cache: 'no-store', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ schemaVersion: 1 })
      }), HANDOFF_TIMEOUT_MS);
    } catch (_) {
      // the next bootstrap carries the restored signal regardless
    }
  }
  state.bootstrapPromise = null;
  state.adsMemberPromise = null;
  await ensureIdentityBootstrap();
  emit('identity:ads', publicAds());
  return publicAds();
}

// --- Flow J: first-party opt-out control ------------------------------------------

async function optOut() {
  state.readingConsentVersion += 1;
  forgetAccountObservation();
  if (state.ads) state.ads = { ...state.ads, id: null, source: null, reason: 'no_durable_identity' };
  writeFirstPartyOptOut(true);
  markWithdrawalPending(true);
  if (state.transport) state.transport.disable('opt_out');
  state.transport = null;
  state.bootstrapCaptureEnabled = false;
  state.accountCheckPending = false;
  state.awaitingCmp = false;
  dropPending('opt_out');
  state.status = { ...state.status, phase: 'SETTLED', identityState: 'BLOCKED_BY_CONSENT', captureEnabled: false, reason: 'first_party_opt_out' };
  emit('identity:ready', publicStatus());
  await finishWithdrawal();
  return publicStatus();
}

async function optIn() {
  if (withdrawalPending() && !await finishWithdrawal()) return publicStatus();
  state.readingConsentVersion += 1;
  writeFirstPartyOptOut(false);
  if (state.issuer && isSameSiteWithIssuer(state.issuer)) {
    try {
      await withTimeout(doFetch(`${state.issuer}${ROUTES.consentRestore}`, {
        method: 'POST', credentials: 'include', cache: 'no-store', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ schemaVersion: 1 })
      }), HANDOFF_TIMEOUT_MS);
    } catch (_) {
      // next bootstrap decides
    }
  }
  state.bootstrapPromise = null;
  state.bootstrapKey = null;
  state.status = initialStatus();
  return ensureIdentityBootstrap();
}

module.exports = {
  DEFAULT_FLAGS,
  PRODUCER_VERSION,
  ROUTES,
  adsOptIn,
  adsOptOut,
  getAdsIdentity,
  getAdsStatus,
  isAdsOptedOut: readAdsOptOut,
  captureArticleView,
  capture,
  configure,
  ensureIdentityBootstrap,
  getIdentityStatus,
  installEmailHandoffBridge,
  isOptedOut: readFirstPartyOptOut,
  optIn,
  optOut,
  requestAuthHandoff,
  observeAuthenticatedSession,
  prepareInsightsIdentity,
  forgetAccountObservation,
  LINKINBIO_SOURCE_EVENT_TYPES,
  requestEmailHandoff,
  trackEvent,
  _state: state
};
